import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Breadcrumb from '../../components/ui/Breadcrumb';
import VendorFilters from './components/VendorFilters';
import VendorTable from './components/VendorTable';
import BulkActions from './components/BulkActions';
import TablePagination from './components/TablePagination';
import InviteVendorModal from '../vendor-approval-workflow/components/InviteVendorModal';
import AssignVendorCodeModal from './components/AssignVendorCodeModal';
import { vendorService, getErrorMessage } from '../../services/apiService';
import { useToast } from '../../components/ui/Toast';
import Icon from '../../components/AppIcon';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import InputDialog from '../../components/ui/InputDialog';
import { useConfirm } from '../../hooks/useConfirm';

const VendorMasterList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [inputDialogState, setInputDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    inputLabel: '',
    inputPlaceholder: '',
    onSubmit: null,
    onCancel: null
  });

  // Default handlers to prevent null reference errors
  const handleDialogCancel = () => {};
  const handleDialogSubmit = () => {};
  
  // File updated - cache cleared, ready for reload
  
  // State management
  const [filters, setFilters] = useState({
    search: '',
    status: '', // Default: All Status
    vendorType: '',
    country: '',
    category: '',
    msmeStatus: '',
    vendorCode: '',
    dateFrom: '',
    dateTo: '',
    turnoverMin: '',
    turnoverMax: ''
  });
  
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [vendors, setVendors] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showImportMessage, setShowImportMessage] = useState(false);
  const [showAssignCodeModal, setShowAssignCodeModal] = useState(false);
  const [selectedVendorForCode, setSelectedVendorForCode] = useState(null);

  // Fetch vendors per page from API (server-side pagination + server filters)
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.vendorType && { vendorType: filters.vendorType }),
        ...(filters.country && { countryOrigin: filters.country }),
        ...(filters.category && { category: filters.category }),
        ...(filters.msmeStatus && { msme_status: filters.msmeStatus }),
        ...(filters.vendorCode && { vendor_code: filters.vendorCode }),
        ...(filters.dateFrom && { date_from: filters.dateFrom }),
        ...(filters.dateTo && { date_to: filters.dateTo }),
        ...(filters.turnoverMin && { turnover_min: filters.turnoverMin }),
        ...(filters.turnoverMax && { turnover_max: filters.turnoverMax }),
        // Exclude vendors with pending approvals - they should only appear in approval workflow
        exclude_pending_approvals: 'true',
      };

      const response = await vendorService.getVendors(params);
      // Filter out REJECTED vendors - they should only appear in the approval workflow's Rejected tab
      const filteredVendors = (response.data || []).filter(
        vendor => vendor.status?.toUpperCase() !== 'REJECTED'
      );
      // Note: serverTotal may include rejected vendors, but they are filtered from display
      // This ensures rejected vendors only appear in the approval workflow's Rejected tab
      setServerTotal(response.total || response.meta?.total || 0);
      setVendors(filteredVendors);
      setError(null);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      const message = getErrorMessage(err);
      setError(message);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filters, sortConfig.key, sortConfig.direction]);

  // Server-side filtering and sorting is handled by the API
  // No client-side filtering needed since we're using server-side pagination and filtering

  // Pagination
  // Server-side pagination and filtering
  const totalItems = serverTotal || vendors.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedVendors = vendors; // already paged from server

  // Event handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '', // Reset to default: All Status
      vendorType: '',
      country: '',
      category: '',
      msmeStatus: '',
      vendorCode: '',
      dateFrom: '',
      dateTo: '',
      turnoverMin: '',
      turnoverMax: ''
    });
    setCurrentPage(1);
  };

  const handleVendorSelect = (vendorId, isSelected) => {
    if (isSelected) {
      setSelectedVendors(prev => [...prev, vendorId]);
    } else {
      setSelectedVendors(prev => prev.filter(id => id !== vendorId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedVendors(paginatedVendors.map(vendor => vendor.id));
    } else {
      setSelectedVendors([]);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const handleViewVendor = (vendor) => {
    navigate(`/vendor-profile-details/${vendor.id}`);
  };

  const handleEditVendor = (vendor) => {
    navigate(`/vendor-profile-details/${vendor.id}`);
  };

  const handleAssignVendorCode = (vendor) => {
    setSelectedVendorForCode(vendor);
    setShowAssignCodeModal(true);
  };

  const handleSubmitVendorCode = async (vendorCode) => {
    if (!selectedVendorForCode) return;
    
    try {
      const response = await vendorService.assignVendorCode(selectedVendorForCode.id, vendorCode || null);
      
      // Extract updated vendor from response
      const responseData = response?.data || response;
      const updatedVendor = responseData?.data || responseData;
      
      // Update only the affected vendor in local state
      setVendors(prevVendors =>
        prevVendors.map(vendor =>
          vendor.id === selectedVendorForCode.id
            ? { ...vendor, ...updatedVendor }
            : vendor
        )
      );
      
      toast.success(`Vendor code assigned successfully to ${selectedVendorForCode.company_name}`);
      setShowAssignCodeModal(false);
      setSelectedVendorForCode(null);
      
      // NO fetchVendors() call - preserves scroll and pagination
    } catch (error) {
      toast.error(`Failed to assign vendor code: ${getErrorMessage(error)}`);
    }
  };

  // Helpers to build a Notepad-friendly text export
  const toTitle = (s) => (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const formatDate = (d) => {
    if (!d) return 'N/A';
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return dt.toLocaleString();
    } catch { return String(d); }
  };
  const formatPrimitiveValue = (key, value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key === 'supplier_type') return formatSupplierType(String(value).toLowerCase());
    if (key === 'supplier_category') return formatSupplierCategory(String(value).toLowerCase());
    if (key === 'msme_status') return formatMsmeStatus(String(value).toLowerCase());
    if (key === 'annual_turnover') return formatAnnualTurnover(value);
    if (key === 'country_origin') return getCountryName(value) || value;
    if (key.endsWith('_at') || key.endsWith('_date')) return formatDate(value);
    return String(value);
  };
  const generateTextFromObject = (obj, indent = 0) => {
    const pad = '  '.repeat(indent);
    if (obj === null || obj === undefined) return `${pad}N/A`;
    if (Array.isArray(obj)) {
      if (obj.length === 0) return `${pad}N/A`;
      return obj
        .map((item, idx) => {
          if (typeof item === 'object' && item !== null) {
            return `${pad}-\n${generateTextFromObject(item, indent + 1)}`;
          }
          return `${pad}- ${String(item)}`;
        })
        .join('\n');
    }
    if (typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .map((rawKey) => {
          const key = String(rawKey);
          const value = obj[key];
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            return `${pad}${toTitle(key)}:\n${generateTextFromObject(value, indent + 1)}`;
          }
          if (Array.isArray(value)) {
            return `${pad}${toTitle(key)}:\n${generateTextFromObject(value, indent + 1)}`;
          }
          return `${pad}${toTitle(key)}: ${formatPrimitiveValue(key, value)}`;
        })
        .join('\n');
    }
    return `${pad}${String(obj)}`;
  };
  const buildVendorText = (vendor) => {
    const headerLines = [
      `Vendor Export`,
      `==============================`,
      `Company: ${vendor.company_name || 'N/A'}`,
      `Vendor Code: ${vendor.vendor_code || 'Pending'}`,
      `Status: ${toTitle(vendor.status) || 'N/A'}`,
      `Created At: ${formatDate(vendor.created_at)}`,
      `Updated At: ${formatDate(vendor.updated_at)}`,
      ''
    ];
    const body = generateTextFromObject(vendor, 0);
    return `${headerLines.join('\n')}\n${body}\n`;
  };

  const handleExportVendor = (vendor) => {
    // Export single vendor data as human-readable .txt
    const text = buildVendorText(vendor);
    const dataBlob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    const safeCode = vendor.vendor_code || vendor.id;
    link.download = `vendor-${safeCode}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = async (format) => {
    if (selectedVendors.length === 0) {
      toast.warning('Please select vendors to export');
      return;
    }

    try {
      toast.info(`Preparing ${format.toUpperCase()} export...`);
      const response = await vendorService.bulkExport(selectedVendors, format);
      
      if (!response) {
        throw new Error('Export failed');
      }

      if (format === 'json') {
        const data = await response.json();
        const dataStr = JSON.stringify(data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `vendors-export-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // For CSV and Excel, download the file directly
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.headers.get('content-disposition')?.split('filename=')[1] || `vendors-export-${new Date().toISOString().split('T')[0]}.${format}`;
        link.click();
        URL.revokeObjectURL(url);
      }

      toast.success(`Successfully exported ${selectedVendors.length} vendors as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Failed to export vendors: ${error.message}`);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedVendors.length === 0) {
      toast.warning('Please select vendors to update');
      return;
    }

    // Show input dialog for status update reason
    const reason = await new Promise((resolve) => {
      setInputDialogState({
        isOpen: true,
        title: 'Status Update Reason',
        message: 'Please provide a reason for the status update (optional):',
        inputLabel: 'Reason',
        inputPlaceholder: 'Enter reason...',
        onSubmit: (value) => {
          setInputDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(value || '');
        },
        onCancel: () => {
          setInputDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(null);
        }
      });
    });

    if (reason === null) return; // User cancelled
    
    try {
      toast.info(`Updating ${selectedVendors.length} vendor(s) to ${status}...`);
      const result = await vendorService.bulkStatusUpdate(selectedVendors, status, reason);

      // Refresh the vendors list
      await fetchVendors();
      
      // Clear selection
      setSelectedVendors([]);
      
      toast.success(`Successfully updated ${result.data?.updated_count || selectedVendors.length} vendors.`);
    } catch (error) {
      console.error('Status update failed:', error);
      toast.error(`Failed to update vendor status: ${getErrorMessage(error)}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVendors.length === 0) {
      toast.warning('Please select vendors to delete');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Vendors',
      message: `Are you sure you want to delete ${selectedVendors.length} selected vendor${selectedVendors.length > 1 ? 's' : ''}? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;

    // Show input dialog for deletion reason
    const reason = await new Promise((resolve) => {
      setInputDialogState({
        isOpen: true,
        title: 'Deletion Reason',
        message: 'Please provide a reason for deletion (optional):',
        inputLabel: 'Reason',
        inputPlaceholder: 'Enter reason...',
        onSubmit: (value) => {
          setInputDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(value || '');
        },
        onCancel: () => {
          setInputDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(null);
        }
      });
    });

    if (reason === null) return; // User cancelled
    
    try {
      toast.info(`Deleting ${selectedVendors.length} vendor(s)...`);
      const result = await vendorService.bulkDelete(selectedVendors, reason);

      // Refresh the vendors list
      await fetchVendors();
      
      // Clear selection
      setSelectedVendors([]);
      
      toast.success(`Successfully deleted ${result.data?.deleted_count || selectedVendors.length} vendors.`);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(`Failed to delete vendors: ${getErrorMessage(error)}`);
    }
  };

  const handleImportVendors = async (file) => {
    if (!file) return;

    try {
      toast.info('Importing vendors...');
      
      const formData = new FormData();
      formData.append('file', file);

      // Note: For now, using a direct API call for import
      // TODO: Add bulkImport to vendorService when backend endpoint is ready
      const response = await fetch('/api/v1/vendors/bulk/import', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Refresh the vendors list
      await fetchVendors();
      
      toast.success(`Successfully imported ${result.imported_count || 0} vendors.`);
      
      if (result.failed_rows && result.failed_rows.length > 0) {
        console.log('Failed rows:', result.failed_rows);
        toast.warning(`${result.failed_rows.length} rows failed to import.`);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(`Failed to import vendors: ${getErrorMessage(error)}`);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb 
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'Vendor Master List', path: '/vendor-master-list' }
        ]} 
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Vendor Master List
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage and view all registered vendors ({totalItems} total)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            onClick={() => setShowInvite(true)}
            iconName="UserPlus"
            iconPosition="left"
          >
            Invite Vendor
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/vendor-approval-workflow')}
            iconName="CheckCircle"
            iconPosition="left"
          >
            Approvals
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <VendorFilters 
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Bulk Actions */}
      {selectedVendors.length > 0 && (
        <BulkActions 
          selectedCount={selectedVendors.length}
          onExport={handleBulkExport}
          onStatusUpdate={handleBulkStatusUpdate}
          onImport={handleImportVendors}
          onBulkDelete={handleBulkDelete}
        />
      )}

      {/* Vendor Table Card — keep filters mounted: loading/error only swap the table body */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 flex flex-col items-center justify-center min-h-[240px]">
              <Icon name="AlertCircle" size={48} className="text-red-600 mb-4" />
              <p className="text-slate-600 mb-4 text-center">{error}</p>
              <Button
                onClick={() => {
                  setError(null);
                  fetchVendors();
                }}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center min-h-[240px] p-6">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4" />
                <p className="text-slate-600">Loading vendors...</p>
              </div>
            </div>
          ) : (
            <VendorTable 
              vendors={paginatedVendors}
              selectedVendors={selectedVendors}
              sortConfig={sortConfig}
              onVendorSelect={handleVendorSelect}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              onViewVendor={handleViewVendor}
              onEditVendor={handleEditVendor}
              onExportVendor={handleExportVendor}
              onAssignVendorCode={handleAssignVendorCode}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination — hide while loading or error to avoid stale totals */}
      {!loading && !error && (
        <TablePagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {showInvite && (
        <InviteVendorModal
          isOpen={showInvite}
          onClose={() => setShowInvite(false)}
        />
      )}

      {showAssignCodeModal && selectedVendorForCode && (
        <AssignVendorCodeModal
          vendor={selectedVendorForCode}
          isOpen={showAssignCodeModal}
          onClose={() => {
            setShowAssignCodeModal(false);
            setSelectedVendorForCode(null);
          }}
          onSubmit={handleSubmitVendorCode}
        />
      )}

      {/* Import Excel - Coming Soon Modal */}
      {showImportMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-6">
              <div className="text-center">
                {/* Icon */}
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
                  <Icon name="AlertTriangle" size={32} className="text-amber-600" />
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  Feature In Progress
                </h3>
                
                {/* Message */}
                <p className="text-slate-600 mb-6 text-sm">
                  Excel import functionality is currently under development. This feature will be available soon.
                </p>
                
                {/* Close Button */}
                <Button
                  onClick={() => setShowImportMessage(false)}
                  className="w-full"
                  variant="default"
                >
                  Got it
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel || handleDialogCancel}
        onConfirm={confirmState.onConfirm || handleDialogSubmit}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />

      {/* Input Dialog */}
      <InputDialog
        isOpen={inputDialogState.isOpen}
        onClose={inputDialogState.onCancel || handleDialogCancel}
        onSubmit={inputDialogState.onSubmit || handleDialogSubmit}
        title={inputDialogState.title}
        message={inputDialogState.message}
        inputLabel={inputDialogState.inputLabel}
        inputPlaceholder={inputDialogState.inputPlaceholder}
      />
    </div>
  );
};

export default VendorMasterList;